import React, { useEffect, useState } from "react";
import { useOutletContext, Link, useNavigate } from "react-router-dom";
import publicApi from "@/lib/publicApi";
import { useSeo, trackFaq } from "@/lib/useSeo";
import {
  Search, ArrowRight, CalendarHeart, Phone, MessageCircle, HelpCircle, HeartHandshake, ShieldCheck,
} from "lucide-react";

const CONTAINER = "max-w-5xl mx-auto px-4 sm:px-6 lg:px-8";
const telHref = (phone) => `tel:${(phone || "").replace(/[^0-9+]/g, "")}`;

export default function Resources() {
  const { content, openChat } = useOutletContext();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const phone = content?.phone;

  useSeo({
    title: "Family Resources & FAQ | MovingMyMama",
    description: "Warm, safe answers to common questions about senior moves, selling a parent's home, senior living, and keeping your loved one safe at home. You don't have to figure this out alone.",
    canonical: typeof window !== "undefined" ? `${window.location.origin}/resources` : undefined,
  });

  useEffect(() => {
    publicApi.get("/public/faq").then(({ data }) => setData(data)).catch(() => setData({ items: [], categories: [] }));
    trackFaq(publicApi, { event: "page_view", slug: "resources" });
  }, []);

  const cats = (data?.categories || []).filter((c) => c.count > 0);
  const featured = (data?.items || []).slice(0, 6);

  const submitSearch = (e) => { e.preventDefault(); navigate(`/resources/faq${q ? `?q=${encodeURIComponent(q)}` : ""}`); };

  return (
    <div data-testid="resources-page">
      {/* Hero */}
      <section className="relative overflow-hidden" style={{ background: "linear-gradient(135deg,#1C4358 0%,#22536B 100%)" }}>
        <div className={`${CONTAINER} relative py-20 sm:py-24 text-center`}>
          <div className="inline-flex items-center gap-2 rounded-full bg-white/12 px-4 py-1.5 text-[13px] font-semibold text-white/90 mb-4"><HelpCircle size={15} /> Family Resources & FAQ</div>
          <h1 className="text-4xl sm:text-5xl font-display font-bold text-white leading-tight">Answers for families, <br className="hidden sm:block" /> when you need them most</h1>
          <p className="mt-4 text-[18px] text-white/85 max-w-2xl mx-auto">You do not have to figure this out alone. Start with a simple answer — and we'll help you find the next right step.</p>
          <form onSubmit={submitSearch} className="mt-7 max-w-xl mx-auto flex items-center gap-2" data-testid="resources-search-form">
            <div className="relative flex-1">
              <Search size={18} className="absolute left-3.5 top-3.5 text-[#4B5563]" />
              <input data-testid="resources-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search questions… e.g. selling a loved one's home"
                className="w-full h-13 py-3.5 pl-11 pr-4 rounded-full text-[16px] bg-white text-[#252221] outline-none" />
            </div>
            <button type="submit" className="mmm-tap h-13 px-6 rounded-full bg-[#9C1E22] hover:bg-[#861A1D] text-white font-semibold">Search</button>
          </form>
        </div>
      </section>

      {/* Category cards */}
      <section className={`${CONTAINER} py-14`}>
        <h2 className="text-lg font-display font-bold text-[#1C4358] mb-5">Browse by topic</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {cats.length === 0 && <p className="text-[15px] text-[#4B5563]">Our FAQ is growing — reach out any time and we'll help your family directly.</p>}
          {cats.map((c) => (
            <Link key={c.key} to={`/resources/faq?category=${c.key}`} data-testid={`resources-cat-${c.key}`}
              onClick={() => trackFaq(publicApi, { event: "category_click", category: c.key })}
              className="group rounded-2xl border border-[#E6EDF2] bg-white p-5 hover:border-[#1C4358]/40 hover:shadow-sm transition-all">
              <div className="flex items-center justify-between">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#1C4358]/8 text-[#1C4358]"><HeartHandshake size={19} /></span>
                <ArrowRight size={17} className="text-[#9AA5B1] group-hover:text-[#9C1E22] transition-colors" />
              </div>
              <div className="mt-3 font-display font-semibold text-[#1C4358] text-[17px]">{c.label}</div>
              <div className="text-[13px] text-[#4B5563] mt-0.5">{c.count} answer{c.count === 1 ? "" : "s"}</div>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured questions */}
      {featured.length > 0 && (
        <section className="bg-[#FBF6EC]"><div className={`${CONTAINER} py-14`}>
          <h2 className="text-lg font-display font-bold text-[#1C4358] mb-5">Common questions</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {featured.map((it) => (
              <Link key={it.id} to={`/resources/faq/${it.slug}`} data-testid={`resources-featured-${it.slug}`}
                className="rounded-xl border border-[#E6EDF2] bg-white p-4 hover:border-[#1C4358]/40 transition-colors flex items-start justify-between gap-3">
                <span className="text-[15px] font-medium text-[#1C4358]">{it.question}</span>
                <ArrowRight size={16} className="text-[#9AA5B1] mt-1 shrink-0" />
              </Link>
            ))}
          </div>
          <div className="text-center mt-6"><Link to="/resources/faq" className="text-[15px] font-semibold text-[#9C1E22] hover:underline">See all questions →</Link></div>
        </div></section>
      )}

      {/* CTA */}
      <section className={`${CONTAINER} py-16 text-center`}>
        <h2 className="text-2xl font-display font-bold text-[#1C4358]">Still have questions?</h2>
        <p className="mt-2 text-[16px] text-[#4B5563] max-w-xl mx-auto">A caring guide can talk through your family's next step — no pressure, no cost.</p>
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <Link to="/book" data-testid="resources-book-btn" onClick={() => trackFaq(publicApi, { event: "faq_to_booking" })} className="mmm-tap inline-flex items-center gap-2 px-6 h-13 rounded-full bg-[#9C1E22] hover:bg-[#861A1D] text-white font-semibold"><CalendarHeart size={18} /> Book Free Consultation</Link>
          {phone && <a href={telHref(phone)} className="mmm-tap inline-flex items-center gap-2 px-6 h-13 rounded-full border border-[#1C4358]/25 text-[#1C4358] font-semibold hover:bg-[#1C4358]/6"><Phone size={18} /> {phone}</a>}
          <button type="button" onClick={() => { trackFaq(publicApi, { event: "faq_to_chat" }); openChat && openChat({ cta_source: "Resources Chat" }); }} data-testid="resources-chat-btn" className="mmm-tap inline-flex items-center gap-2 px-6 h-13 rounded-full border border-[#1C4358]/25 text-[#1C4358] font-semibold hover:bg-[#1C4358]/6"><MessageCircle size={18} /> Start Live Chat</button>
        </div>
      </section>
    </div>
  );
}
